import Cookies from 'js-cookie'

import { categories, maxLogoFileSizeInBytes } from './constants'
import { formatDomainBeforeSend, normalizeLogoData, normalizeStore } from './helpFunctions'
import {
  SET_ERROR,
  SET_SHOP,
  UPDATE_EDIT_SHOP_STATE,
  UPDATE_SHOP_ATTRIBUTE,
  UPDATE_STRIPE,
} from './mutation-types'

import { RepositoryFactory } from '@/legacy/repositories/RepositoryFactory'
import StoreRepository from '@/legacy/repositories/storeRepository'
import * as types from '@/legacy/store/mutation-types'
import { stripe } from '@/lib/helpers/stripe'

const AdvertiserRepository = RepositoryFactory.get('advertiser')

const actionTypes = {
  REMOVE: 'REMOVE',
  ADD: 'ADD',
}

export default {
  namespaced: true,
  state: {
    shop: {
      id: getShopIdFromCookie(),
      config: getShopConfigFromCookie() || [],
      name: getShopNameFromCookie(),
      email: null,
      companyName: null,
      trafficSources: [],
      domains: [],
      categories: [],
      currency: null,
      locale: null,
      image: null,
      logoUrl: null,
      hidden: false,
      storeLogo: null,
      originalLogo: null,
      clearingNumber: null,
      card: null,
      advertiserUsesCpa: false,
      userCanEditAllInfluencers: false, // User permission for this shop
      paymentType: null,
      stripeSubscriptionStatus: getStripeSubscriptionStatusFromCookie(),
      stripCustomerId: null,
    },
    strings: {
      savedSuccessMsg: 'Saved Successfully.',
      onlyInt: '* Only integers are allowed.',
      invalidDomain: 'Invalid domain.',
      logotypeError: 'Could not save the logotype.',
      saveError: 'Could not save the settings.',
      invalidInputFields: 'There are invalid fields. Please, fix them before saving.',
      headTitleNoStore: 'Did not find any store to edit',
    },
    statusMessage: [],
    loading: false,
    isFetching: false,
    isFetched: false,
    savingShopDetails: false,
    savingPaymentDetails: false,
    loadingPaymentForm: false,
    headTitle: '',
    formErrors: [],
    categories: categories,
    stripe: {
      card: null,
      stripe: null,
      elements: null,
    },
    updateCardErrorMessage: null,
    currency: null,
    error: null,
  },
  getters: {
    hasShopSummary: (state) => Boolean(state.shop.id),
    shop: (state) => state.shop,
    headTitle: (state) => state.headTitle,
    shopId: (state) => state.shop.id,
    storeGroupId: (state) => state.shop.storeGroupId,
    storeGroups: (state) => state.shop.storeGroups,
    shopTrafficSources: (state) => state.shop.trafficSources,
    domains: (state) => state.shop.domains,
    logoUrl: (state) => state.shop.logoUrl,
    image: (state) => state.shop.image,
    maxLogoSizeInKB: () => maxLogoFileSizeInBytes / 1000,
    selectedCategories: (state) => state.shop.categories,
    formErrors: (state) => state.formErrors,
    categories: (state) => state.categories,
    loading: (state) => state.loading,
    savingShopDetails: (state) => state.savingShopDetails,
    savingPaymentDetails: (state) => state.savingPaymentDetails,
    hidden: (state) => state.shop.hidden,
    statusMessage: (state) => state.statusMessage,
    card: (state) => state.shop.card?.card,
    clearingNumber: (state) => state.shop.clearingNumber,
    onlyIntMsg: (state) => state.strings.onlyInt,
    stripe: (state) => state.stripe,
    updateCardErrorMessage: (state) => state.updateCardErrorMessage,
    advertiserUsesCpa: (state) => state.shop.advertiserUsesCpa,
    hideEdit: (state) => isHide(state, 'edit'),
    hideCosts: (state) => isHide(state, 'cost'),
    hideClicks: (state) => isHide(state, 'clicks'),
    hideMedia: (state) => isHide(state, 'media'),
    hideFindInfluencers: (state) => isHide(state, 'find_influencers'),
    hideSales: (state) => isHide(state, 'sales'),
    hideUsers: (state) => isHide(state, 'users'),
    userCanEditAllInfluencers: (state) => state.shop.userCanEditAllInfluencers,
    currency: (state) => state.shop.currency,
    locale: (state) => state.shop.locale,
    paymentType: (state) => state.shop.paymentType,
    stripeSubscriptionStatus: (state) => state.shop.stripeSubscriptionStatus,
    loadingPaymentForm: (state) => state.loadingPaymentForm,
    isDirectStore: (state) => state.shop.isDirectStore,
    isFetching: (state) => state.isFetching,
    isFetched: (state) => state.isFetched,
    isWhiteLabel: (state) => state.shop.isWhiteLabel,
  },
  mutations: {
    [UPDATE_EDIT_SHOP_STATE]: (state, { type, value }) => (state[type] = value),
    [SET_SHOP]: (state, shop) => (state.shop = { ...shop }),
    [UPDATE_SHOP_ATTRIBUTE]: (state, { type, value }) => (state.shop[type] = value),
    [UPDATE_STRIPE]: (state, { card, stripe, elements }) =>
      (state.stripe = { card, stripe, elements }),
    [SET_ERROR]: (state, value) => (state.error = value),
  },
  actions: {
    setShopFromPinia({ commit }, responseData) {
      commit(SET_SHOP, normalizeStore(responseData))
    },
    async createAdvertiserRepresentative({ commit, rootState }, body) {
      try {
        const { data } = await StoreRepository.createAdvertiserRepresentative(
          body,
          rootState.user.token,
        )

        return { data }
      } catch (error) {
        commit(types.SET_ERROR, error)
        return { error }
      }
    },
    async createStore({ commit, rootState }, body) {
      try {
        const { data } = await StoreRepository.createStore(body, rootState.user.token)

        return { data }
      } catch (error) {
        commit(types.SET_ERROR, error)
        return { error }
      }
    },
    fetchShop({ rootState, commit, state }) {
      commit(UPDATE_EDIT_SHOP_STATE, {
        type: 'loading',
        value: true,
      })
      if (!state.isFetched) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'isFetching',
          value: true,
        })
      }
      AdvertiserRepository.getStore(rootState.user.token, state.shop.id)
        .then((response) => {
          const shop = normalizeStore(response.data)
          commit(SET_SHOP, shop)
          Cookies.set('store_info', JSON.stringify({ ...shop }), { expires: 365 })
        })
        .finally(() => {
          commit(UPDATE_EDIT_SHOP_STATE, {
            type: 'loading',
            value: false,
          })
          commit(UPDATE_EDIT_SHOP_STATE, {
            type: 'isFetching',
            value: false,
          })
          commit(UPDATE_EDIT_SHOP_STATE, {
            type: 'isFetched',
            value: true,
          })
        })
    },
    async sendForm({ state, commit, rootState, dispatch }, withTrafficSources = false) {
      commit(UPDATE_EDIT_SHOP_STATE, {
        type: 'statusMessage',
        value: '',
      })
      if (state.formErrors.length > 0 || state.shop.domains.length === 0) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'statusMessage',
          value: [state.strings.invalidInputFields],
        })
        return
      }
      commit(UPDATE_EDIT_SHOP_STATE, {
        type: 'savingShopDetails',
        value: true,
      })

      await AdvertiserRepository.updateStore(
        rootState.user.token,
        state.shop.id,
        normalizeSendData(state.shop, withTrafficSources),
      )
        .then(async (response) => {
          if (state.shop.logoUrl && state.shop.originalLogo !== state.shop.logoUrl) {
            await AdvertiserRepository.updateStoreLogo(
              rootState.user.token,
              state.shop.id,
              normalizeLogoData(state.shop.storeLogo),
            )
              .then(() => {
                commit(UPDATE_EDIT_SHOP_STATE, {
                  type: 'savingShopDetails',
                  value: false,
                })
                commit(UPDATE_EDIT_SHOP_STATE, {
                  type: 'statusMessage',
                  value: [state.strings.savedSuccessMsg],
                })
              })
              .catch(() => {
                commit(UPDATE_EDIT_SHOP_STATE, {
                  type: 'statusMessage',
                  value: [state.strings.logotypeError],
                })
                commit(UPDATE_EDIT_SHOP_STATE, {
                  type: 'savingShopDetails',
                  value: false,
                })
              })
          } else {
            commit(UPDATE_EDIT_SHOP_STATE, {
              type: 'savingShopDetails',
              value: false,
            })
            commit(SET_SHOP, normalizeStore(response.data))
            commit(UPDATE_EDIT_SHOP_STATE, {
              type: 'statusMessage',
              value: [state.strings.savedSuccessMsg],
            })
          }
        })
        .catch((err) => {
          commit(UPDATE_EDIT_SHOP_STATE, {
            type: 'statusMessage',
            value: [state.strings.saveError],
          })
          if (err.response.data.errors) {
            dispatch('validateResponseError', err.response.data.errors)
          }
          commit(UPDATE_EDIT_SHOP_STATE, {
            type: 'savingShopDetails',
            value: false,
          })
        })
    },
    validateResponseError: ({ commit, state }, errors) => {
      const yourRegex = /domain/g
      if (Object.keys(errors).some((e) => yourRegex.test(e))) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'statusMessage',
          value: [...state.statusMessage, state.strings.invalidDomain],
        })
      }
    },
    saveCosts({ commit, dispatch }, sourcesCosts) {
      commit(UPDATE_SHOP_ATTRIBUTE, {
        type: 'trafficSources',
        value: sourcesCosts,
      })
      dispatch('sendForm', true)
    },

    updateDomain: ({ commit, dispatch, state }, { text, domain }) => {
      const newData = state.shop.domains.map((item) => {
        if (domain.id === item.id) {
          return {
            id: domain.id,
            text,
          }
        }
        return item
      })
      commit(UPDATE_SHOP_ATTRIBUTE, {
        type: 'domains',
        value: newData,
      })
      dispatch('validateInput', {
        type: `domain${domain.id}-empty`,
        value: text,
      })
    },
    deleteDomain: ({ state, commit, dispatch }, domain) => {
      const domains = state.shop.domains
      const newData = domains.filter((item) => item.id !== domain.id)
      commit(UPDATE_SHOP_ATTRIBUTE, {
        type: 'domains',
        value: newData,
      })
      const checkFormErrors = (i, type) => {
        dispatch('toggleFormErrors', {
          type: `domain${i}-${type}`,
          action: actionTypes.REMOVE,
        })
      }
      checkFormErrors(domain.id, 'invalid')
      checkFormErrors(domain.id, 'empty')
    },
    addDomain: ({ commit, state, dispatch }) => {
      const domains = state.shop.domains
      const id = domains.length ? domains[domains.length - 1].id + 1 : 0
      validDomains(domains, dispatch)
      commit(UPDATE_SHOP_ATTRIBUTE, {
        type: 'domains',
        value: [...domains, { id, text: '' }],
      })
      dispatch('validateInput', { type: `domain${id}-empty`, value: '' })
    },
    updateHidden: ({ commit }, value) => {
      commit(UPDATE_SHOP_ATTRIBUTE, {
        type: 'hidden',
        value: value,
      })
    },
    updateSelectedCategories: ({ commit, state, dispatch }, event) => {
      let newData = state.shop.categories
      const value = Number(event.target.value)
      if (!state.shop.categories.includes(value)) {
        newData.push(value)
      } else {
        newData = state.shop.categories.filter((category) => category !== value)
      }
      commit(UPDATE_SHOP_ATTRIBUTE, {
        type: 'categories',
        value: newData,
      })
      dispatch('validateInput', {
        type: 'categories',
        value: state.shop.categories,
        validatorFunc: () => {
          return state.shop.categories.length > 0
        },
      })
    },
    updateStoreLogo: ({ commit, dispatch }, newFile) => {
      const file = newFile.file
      if (!file) {
        return
      }
      if (file.size > maxLogoFileSizeInBytes) {
        dispatch('toggleFormErrors', {
          type: 'storeLogo',
          action: actionTypes.ADD,
        })
        return
      } else {
        dispatch('toggleFormErrors', {
          type: 'storeLogo',
          action: actionTypes.REMOVE,
        })
      }
      commit(UPDATE_SHOP_ATTRIBUTE, { type: 'storeLogo', value: file })
      commit(UPDATE_SHOP_ATTRIBUTE, {
        type: 'logoUrl',
        value: URL.createObjectURL(file),
      })
    },
    toggleFormErrors: ({ state, commit }, { type, action }) => {
      if (action === actionTypes.ADD && !state.formErrors.includes(type)) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'formErrors',
          value: [...state.formErrors, type],
        })
      }
      if (action === actionTypes.REMOVE && state.formErrors.includes(type)) {
        const newData = state.formErrors.filter((item) => item !== type)
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'formErrors',
          value: newData,
        })
      }
    },
    validateInput: ({ state, commit }, { type, value, validatorFunc }) => {
      let validatorType = value
      if (validatorFunc) {
        validatorType = validatorFunc(value)
      }
      if (!validatorType && !state.formErrors.includes(type)) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'formErrors',
          value: [...state.formErrors, type],
        })
      } else if (!!validatorType && state.formErrors.includes(type)) {
        const newData = state.formErrors.filter((error) => error !== type)
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'formErrors',
          value: newData,
        })
      }
    },
    setupStripeUpdate: function ({ commit }) {
      const elements = stripe.elements()

      const card = elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            iconColor: '#666EE8',
            color: '#31325F',
            lineHeight: '40px',
            fontWeight: 300,
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSize: '18px',

            '::placeholder': {
              color: '#5f656b',
            },
          },
        },
      })
      commit(UPDATE_STRIPE, {
        stripe: stripe,
        card: card,
      })
    },
    async updateCard({ commit, state, rootState, dispatch }) {
      commit(UPDATE_EDIT_SHOP_STATE, {
        type: 'savingPaymentDetails',
        value: true,
      })
      if (state.updateCardErrorMessage) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'updateCardErrorMessage',
          value: null,
        })
      }
      // get session key.
      const { data } = await AdvertiserRepository.getStripeCustomerSession(
        rootState.user.token,
        rootState.shop.shop.id,
      )

      const { error } = await state.stripe.stripe.confirmCardSetup(data.intent.client_secret, {
        payment_method: {
          card: state.stripe.card,
        },
      })

      if (error) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'updateCardErrorMessage',
          value: error.message,
        })
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'savingPaymentDetails',
          value: false,
        })
      } else {
        commit(UPDATE_STRIPE, {
          stripe: null,
          card: null,
          elements: null,
        })
        dispatch('fetchShop', state.shop.id)
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'savingPaymentDetails',
          value: false,
        })
      }
    },
    async setupStripeSubscription({ commit, state }) {
      try {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'loadingPaymentForm',
          value: true,
        })

        // Create a subscription for previous created customer
        const {
          data: { clientSecret, subscriptionId },
        } = await AdvertiserRepository.createStripeSubscription(state.shop.stripCustomerId)
        commit(UPDATE_SHOP_ATTRIBUTE, {
          type: 'subscriptionId',
          value: subscriptionId,
        })
        const options = { clientSecret }
        const elements = stripe.elements(options)
        const card = elements.create('payment')
        commit(UPDATE_STRIPE, { stripe, card, elements })
      } catch {
        // swallow
      } finally {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'loadingPaymentForm',
          value: false,
        })
      }
    },
    async subscribe({ commit, state, dispatch }) {
      commit(UPDATE_EDIT_SHOP_STATE, {
        type: 'savingPaymentDetails',
        value: true,
      })
      commit(UPDATE_EDIT_SHOP_STATE, {
        type: 'updateCardErrorMessage',
        value: null,
      })

      // Temp save the state to use after the payment redirect
      localStorage.setItem('editState', JSON.stringify(state))

      try {
        const result = await state.stripe.stripe.confirmPayment({
          elements: state.stripe.elements,
          confirmParams: {
            return_url: `${window.location.href}`,
          },
        })
        if (result.error) {
          commit(UPDATE_EDIT_SHOP_STATE, {
            type: 'updateCardErrorMessage',
            value: result.error.message,
          })
          return
        }

        dispatch('fetchShop', state.shop.id)
      } catch (error) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'updateCardErrorMessage',
          value: error.message,
        })
      } finally {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'savingPaymentDetails',
          value: false,
        })
      }
    },
    async handleRedirectStatus({ commit, state }, clientSecret) {
      try {
        if (!clientSecret) {
          return
        }
        const editState = JSON.parse(localStorage.getItem('editState'))

        // Retrieve the PaymentIntent
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret)

        if (paymentIntent.status === 'succeeded') {
          // Verify the stripe subscription it will either activate the subscription
          // if the payment went through or it will throw an error in case of invalid data.
          await AdvertiserRepository.verifySubscription(
            state.shop.id,
            editState.shop.subscriptionId,
          )
          // Remove the Temp saved status, as it no longer needed upon the registration
          localStorage.setItem('editState', JSON.stringify({}))
        }
      } catch (error) {
        commit(UPDATE_EDIT_SHOP_STATE, {
          type: 'updateCardErrorMessage',
          value: error.message,
        })
      }
    },
  },
}

const normalizeSendData = (shop, withTrafficSources) => {
  const normalizedData = {
    domains: shop.domains.map((item) => formatDomainBeforeSend(item.text)),
    hidden: Number(shop.hidden),
    categories: shop.categories,
  }

  if (withTrafficSources) {
    const trafficSources = []
    for (let i = 0; i < shop.trafficSources.length; i++) {
      const temp: {
        source: string
        cpc?: number | string
        cpa?: number | string
      } = {
        source: shop.trafficSources[i].source,
      }
      if (shop.trafficSources[i].cpc) {
        // @ts-expect-error todo fix me
        temp.cpc = (Number(shop.trafficSources[i].cpc).toFixed(2) * 100).toFixed(0)
      } else {
        temp.cpc = 0
      }
      if (shop.trafficSources[i].cpa) {
        temp.cpa = (Number(shop.trafficSources[i].cpa.toFixed(0)) / 100).toFixed(2)
      } else {
        temp.cpa = 0
      }
      trafficSources.push(temp)
    }

    // @ts-expect-error todo fix me
    normalizedData.traffic_sources_costs = trafficSources
  }

  return normalizedData
}

const validDomains = (domains, dispatch) => {
  const emptyDomains = domains.filter((item) => {
    if (!item.text) {
      dispatch('validateInput', {
        type: `domain${item.id}-empty`,
        value: item.text,
      })
    }
    return item
  })
  return emptyDomains.length === 0 && domains.length > 0
}

function getShopIdFromCookie() {
  if (Cookies.get('store_info')) {
    return JSON.parse(Cookies.get('store_info')).id
  } else {
    return null
  }
}

function getShopConfigFromCookie() {
  if (Cookies.get('store_info')) {
    return JSON.parse(Cookies.get('store_info')).config
  } else {
    return null
  }
}

function getShopNameFromCookie() {
  if (Cookies.get('store_info')) {
    return JSON.parse(Cookies.get('store_info')).name
  } else {
    return null
  }
}

function getStripeSubscriptionStatusFromCookie() {
  if (Cookies.get('store_info')) {
    const data = JSON.parse(Cookies.get('store_info'))
    return data.stripeSubscriptionStatus || data.stripe_subscription_status
  } else {
    return null
  }
}

function isHide(state, attribute) {
  if (state.shop.config?.hide) {
    return state.shop.config.hide.includes(attribute)
  }

  return false
}
